import React, { useEffect, useState } from 'react';

import BottomSheet from 'components/DesignSystem/BottomSheet';
import Searcher from 'components/DesignSystem/Searcher';
import Snackbar from 'components/DesignSystem/Snackbar';
import DatePicker from 'components/DatePicker';
import Section from 'components/Meteorology/Shared/ControlPanelSheet/Section';
import OptionRow from 'components/Meteorology/Shared/ControlPanelSheet/OptionRow';
import SheetActionButton from 'components/Layout/MobileShell/SheetActionButton';
import useDetailedViewMobile from 'hooks/Meteorology/Agro/WaterBalanceCulture/DetailedView/Mobile/useDetailedViewMobile';

import './index.scss';

type SheetSection = 'group' | 'location' | 'culture' | 'emergency' | 'period' | 'soil' | '';

export default function ControlPanelSheet({
  open,
  dismiss,
}: {
  open: boolean;
  dismiss: () => void;
}) {
  const [openSection, setOpenSection] = useState<SheetSection>('group');
  const [groupSearch, setGroupSearch] = useState('');
  const [locationSearch, setLocationSearch] = useState('');

  const {
    group, location, culture, emergency, period, soil, triggerRequest, clearControlPanel,
  } = useDetailedViewMobile();

  useEffect(() => {
    if (open) setOpenSection('group');
  }, [open]);

  const toggleSection = (section: SheetSection) => () => (
    setOpenSection((current) => (current === section ? '' : section))
  );

  const confirm = () => {
    if (triggerRequest) triggerRequest();
    dismiss();
  };

  const clear = () => {
    clearControlPanel();
    dismiss();
  };

  const pattern = (search: string) => new RegExp(search.split('').join('.*'), 'i');
  const groupPattern = pattern(groupSearch);
  const locationPattern = pattern(locationSearch);

  const selectedGroupName = group.availableGroups
    ?.find((g) => g.id === group.selectedGroup)?.name;
  const selectedLocationName = location.availableLocations
    .find((l) => l.id === location.selectedLocation)?.name;
  const selectedCultureLabel = culture.availableCultures
    ?.find((c) => c.id === culture.selectedCulture)?.label;
  const selectedSoilLabel = soil.soilTypes?.find((s) => s.id === soil.selectedSoil)?.label;
  const emergencyLabel = emergency.emergencyDate
    ? emergency.emergencyDate.toLocaleDateString('pt-BR')
    : undefined;
  const periodLabel = period.startDate && period.endDate
    ? `${period.startDate.toLocaleDateString('pt-BR')} - ${period.endDate.toLocaleDateString('pt-BR')}`
    : undefined;

  return (
    <BottomSheet
      open={open}
      dismiss={dismiss}
      title="Painel de controle"
      className="agro-wbc-dv-control-panel-sheet"
    >
      <div className="agro-wbc-dv-sheet-body">
        <Section
          label="Grupo"
          valueText={selectedGroupName}
          open={openSection === 'group'}
          onToggle={toggleSection('group')}
        >
          <Searcher value={groupSearch} onChange={(e) => setGroupSearch(e.target.value)} />
          {(!group.availableGroups || group.availableGroups.length === 0) && (
            <Snackbar className="mt-2">Nenhum grupo disponível.</Snackbar>
          )}
          {group.availableGroups?.filter((g) => groupPattern.test(g.name)).map((g) => (
            <OptionRow
              key={g.id}
              label={g.name}
              selected={g.id === group.selectedGroup}
              onSelect={group.setGroup(g.id)}
            />
          ))}
        </Section>

        <Section
          label="Local"
          valueText={selectedLocationName}
          open={openSection === 'location'}
          onToggle={toggleSection('location')}
        >
          <Searcher value={locationSearch} onChange={(e) => setLocationSearch(e.target.value)} />
          {location.availableLocations.length === 0 && (
            <Snackbar className="mt-2">
              {group.selectedGroup === undefined
                ? 'Selecione um grupo primeiro.'
                : 'Nenhum local disponível para o grupo selecionado.'}
            </Snackbar>
          )}
          {location.availableLocations.filter((l) => locationPattern.test(l.name)).map((l) => (
            <OptionRow
              key={l.id}
              label={l.name}
              selected={l.id === location.selectedLocation}
              onSelect={() => location.setLocation(l.id)}
            />
          ))}
        </Section>

        <Section
          label="Cultura"
          valueText={selectedCultureLabel}
          open={openSection === 'culture'}
          onToggle={toggleSection('culture')}
        >
          {(!culture.availableCultures || culture.availableCultures.length === 0) && (
            <Snackbar className="mt-2">Nenhuma cultura disponível.</Snackbar>
          )}
          {culture.availableCultures?.map((c) => (
            <OptionRow
              key={c.id}
              label={c.label}
              selected={c.id === culture.selectedCulture}
              onSelect={() => culture.setCulture(c.id)}
            />
          ))}
        </Section>

        <Section
          label="Data da Emergência"
          valueText={emergencyLabel}
          open={openSection === 'emergency'}
          onToggle={toggleSection('emergency')}
        >
          <DatePicker
            selected={emergency.emergencyDate}
            onChange={emergency.changeEmergencyDate}
            includeDateIntervals={emergency.dateIntervals}
            inline
          />
        </Section>

        <Section
          label="Período de Análise"
          valueText={periodLabel}
          open={openSection === 'period'}
          onToggle={toggleSection('period')}
        >
          <DatePicker
            selectsRange
            startDate={period.startDate}
            endDate={period.endDate}
            onChange={period.changePeriod}
            includeDateIntervals={period.dateIntervals}
            inline
          />
        </Section>

        <Section
          label="Tipo de Solo · opcional"
          valueText={selectedSoilLabel}
          open={openSection === 'soil'}
          onToggle={toggleSection('soil')}
        >
          <p className="agro-wbc-dv-sheet-soil-hint">
            Se não escolher, aplicamos o CAD padrão calculado para a localidade.
          </p>
          {(!soil.soilTypes || soil.soilTypes.length === 0) && (
            <Snackbar className="mt-2">Nenhum tipo de solo disponível.</Snackbar>
          )}
          {soil.soilTypes?.map((s) => (
            <OptionRow
              key={s.id}
              label={s.label}
              selected={s.id === soil.selectedSoil}
              onSelect={() => soil.setSoil(s.id)}
            />
          ))}
        </Section>
      </div>

      <div className="agro-wbc-dv-sheet-footer">
        <SheetActionButton
          variant="confirm"
          disabled={triggerRequest === undefined}
          onClick={confirm}
        >
          Realizar consulta
        </SheetActionButton>
        <SheetActionButton variant="clear" onClick={clear}>
          Limpar filtros
        </SheetActionButton>
      </div>
    </BottomSheet>
  );
}
